/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React from 'react';
import {
  Image,
  ImageStyle,
  Platform,
  ScrollView,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { IconButton, Icon } from 'rsuite';
import { titleCase } from 'title-case';
import { GridItemV, IconV } from './components';
import { TextVW } from '@vlinder-web/text-vfw-widget-react';
import { ScreenWidth } from 'react-native-elements/dist/helpers';
const hex2rgb = require('hex2rgb');
const is = require('is_js');

const getRGB = (color: string) => {
  const bg = hex2rgb(color, { rgbStringDefault: 'transparent' }).rgb;
  return `rgba(${bg[0]}, ${bg[1]}, ${bg[2]}, 0.4)`;
};

const hitSlop = { top: 15, right: 15, left: 15, bottom: 15 };

interface ChipVProps {
  chipItems: any[];
  mainContainerStyle?: ViewStyle;
  titleStyle?: TextStyle;
  chipItemContainerStyle?: ViewStyle;
  chipItemBgColor?: String;
  mainContainerHeight?: number;
  mainBgColor?: string;
  chipItemHeight?: number;
  chipItemRadius?: number;
  imgContainerStyle?: ViewStyle;
  imgStyle?: ImageStyle;
  imgTintColor?: string;
  chipBorderWidth?: number;
  chipBorderColor?: string;
  imgBgColor?: string;
  titleColor?: string;
  onChipItemPress?: (key: any) => void;
  rightIconContainer?: ViewStyle;
}

export const ChipV = ({
  chipItems,
  mainContainerStyle,
  titleStyle,
  chipItemContainerStyle,
  chipItemBgColor,
  mainContainerHeight,
  mainBgColor,
  chipItemHeight,
  chipItemRadius,
  imgContainerStyle,
  imgStyle,
  imgTintColor,
  chipBorderWidth,
  chipBorderColor,
  imgBgColor,
  titleColor,
  onChipItemPress,
  rightIconContainer,
}: ChipVProps): JSX.Element => {
  const _renderImage = (imageUri: string) => {
    if (is.existy(imageUri) && is.not.empty(imageUri) && is.url(imageUri)) {
      return (
        <GridItemV colSpan={ScreenWidth * 0.1}>
          <View
            // @ts-ignore
            style={StyleSheet.flatten([
              styles.imgContainer,
              imgContainerStyle,
              imgBgColor && { backgroundColor: getRGB(imgBgColor) },
            ])}
          >
            <Image
              source={{ uri: imageUri }}
              resizeMode={'contain'}
              // @ts-ignore
              style={StyleSheet.flatten([
                styles.imgStyle,
                imgStyle,
                imgTintColor && { tintColor: imgTintColor },
              ])}
            />
          </View>
        </GridItemV>
      );
    }
  };

  const _renderTitle = (
    title: string,
    imageUri: string,
    rightIconName: string
  ) => {
    if (title && is.existy(title) && is.not.empty(title)) {
      return (
        // <GridItemV colSpan={_returnColValue(imageUri, title, rightIconName)}>
        <TextVW
          type={'footnote'}
          children={
            title?.length > 28
              ? `${titleCase(title.substring(0, 28))}...`
              : titleCase(title)
          }
          ellipsizeMode={'tail'}
          numberOfLines={1}
          style={StyleSheet.flatten([
            styles.titleStyle,
            titleStyle,
            titleColor && { color: titleColor },
            // {textAlign: imageUri ? 'left' : 'center'},
            // {left: imageUri ? 3 : 0},
          ])}
        />
        // </GridItemV>
      );
    }
  };

  const _renderRightIcon = (
    rightIconName,
    rightIconType,
    rightIconColor,
    rightIconSize,
    index
  ) => {
    if (
      rightIconName &&
      is.existy(rightIconName) &&
      is.not.empty(rightIconName)
    ) {
      return (
        <GridItemV colSpan={ScreenWidth * 0.1}>
          <IconV
            name={rightIconName}
            type={rightIconType}
            color={rightIconColor}
            size={rightIconSize}
            containerStyle={StyleSheet.flatten([
              styles.rightIconContainer,
              rightIconContainer && rightIconContainer,
            ])}
            onPress={() => onChipItemPress && onChipItemPress(index)}
          />
        </GridItemV>
      );
    }
  };

  const _getChipItems = () => {
    const data = [];
    if (chipItems && Array.isArray(chipItems) && chipItems.length > 0) {
      for (let i = 0; i < chipItems.length && chipItems[i]; ++i) {
        data.push(
          <TouchableOpacity
            hitSlop={hitSlop}
            // @ts-ignore
            style={StyleSheet.flatten([
              styles.chipItemContainer,
              chipItemContainerStyle,
              chipItemBgColor && { backgroundColor: chipItemBgColor },
              chipItemHeight && { height: chipItemHeight },
              chipItemRadius && { borderRadius: chipItemRadius },
              chipBorderWidth && { borderWidth: chipBorderWidth },
              chipBorderColor && { borderColor: chipBorderColor },
            ])}
            key={i}
            // @ts-ignore
            onPress={() => onChipItemPress && onChipItemPress(chipItems[i])}
          >
            <div>
              {_renderImage(chipItems[i]?.imageUri)}

              {_renderTitle(
                chipItems[i].title,
                chipItems[i]?.imageUri,
                chipItems[i].rightIconName
              )}

              {/* {_renderRightIcon(
                chipItems[i].rightIconName,
                chipItems[i].rightIconType,
                chipItems[i].rightIconColor,
                chipItems[i].rightIconSize,
                chipItems[i]
              )} */}
              <div
                style={{ color: titleColor, textAlign: 'center', fontSize: 12 }}
              >{`Evidence - ${i + 1}`}</div>
              <div
                style={{
                  justifyContent: 'center',
                  display: 'flex',
                  paddingTop: '5px',
                }}
              >
                <IconButton
                  style={{
                    backgroundColor: '#025efd',
                    color: '#ffffff',
                  }}
                  icon={
                    <Icon icon='link' style={{ backgroundColor: '#025efd' }} />
                  }
                  placement='right'
                >
                  {'View Evidence'}
                </IconButton>
              </div>
            </div>
          </TouchableOpacity>
        );
      }
    }
    return data;
  };

  const _returnColValue = (
    imgUri: string,
    title: string,
    rightIcon: string
  ) => {
    if (imgUri && rightIcon && title.length <= 6) {
      return ScreenWidth * 0.15;
    } else if (imgUri && rightIcon && title.length > 6) {
      return ScreenWidth * 0.18;
    } else if (imgUri && title.length <= 6) {
      return ScreenWidth * 0.16;
    } else if (imgUri && title.length > 6) {
      return ScreenWidth * 0.23;
    } else if (title.length < 4) {
      return ScreenWidth * 0.18;
    } else if (imgUri) {
      return ScreenWidth * 0.2;
    } else {
      return ScreenWidth * 0.18;
    }
  };

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      // @ts-ignore
      contentContainerStyle={StyleSheet.flatten([
        styles.container,
        mainContainerStyle,
        mainContainerHeight && { height: mainContainerHeight },
        mainBgColor && { backgroundColor: mainBgColor },
      ])}
    >
      {_getChipItems()}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    // flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    // alignSelf: 'center',
    flexDirection: 'row',
    paddingRight: 20,
    margin: 5,
  },
  chipItemContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    // flexDirection: 'row',
    elevation: 8,
    margin: 10,
    shadowColor: getRGB('#000000'),
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 2,
    ...Platform.select({
      android: {
        elevation: 2,
      },
    }),
  },
  titleStyle: {
    letterSpacing: 0.9,
    paddingHorizontal: 10,
  },
  imgContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    height: 30,
    width: 30,
    borderRadius: 30 / 2,
    elevation: 10,
    ...Platform.select({
      android: {
        elevation: 0,
      },
    }),
  },
  imgStyle: {
    alignSelf: 'center',
    resizeMode: 'contain',
    height: 20,
    width: 20,
  },
  rightIconContainer: {
    // marginHorizontal: 2,
    borderRadius: 20,
  },
});

ChipV.defaultProps = {
  mainBgColor: 'transparent',
  chipItemBgColor: '#fff',
  mainContainerHeight: 190,
  chipItemHeight: 150,
  chipItemRadius: 20,
  imgBgColor: '#d3d3d3',
  // chipBorderWidth: 1,
  // chipBorderColor: 'green',
  onChipItemPress: () => {},
} as Partial<ChipVProps>;
